import { apiClient } from '../lib/apiClient';

/**
 * Admin → Release Notes (backend src/routes/adminReleaseNotes.routes.ts).
 * Errors (ApiError.code): 409 NOT_EDITABLE / ALREADY_SENDING / NOT_READY /
 * INCOMPLETE / VERSION_EXISTS / NO_SOURCE_ITEMS / COMBINED / NOT_COMBINABLE / NOT_COMBINED,
 * 503 EMAIL_NOT_CONFIGURED.
 */
export type ReleaseNoteGenerationStatus = 'pending' | 'ai' | 'fallback' | 'failed' | 'manual';
export type ReleaseNoteStatus = 'draft' | 'sending' | 'sent';

export interface ReleaseSourceItem {
  number: number;
  title: string;
  labels: string[];
  body: string;
}

export interface ReleaseNote {
  id: string;
  version: string;
  /** Semantic version of the deploy ("1.0.0"); null for manual and pre-versioning notes. */
  appVersion: string | null;
  gitSha: string | null;
  previousSha: string | null;
  deployedAt: string | null;
  sourceItems: ReleaseSourceItem[];
  titleDe: string;
  bodyDe: string;
  titleEn: string;
  bodyEn: string;
  generationStatus: ReleaseNoteGenerationStatus;
  status: ReleaseNoteStatus;
  createdAt: string;
  updatedAt: string;
  sentAt: string | null;
  sentBy: string | null;
  recipientCount: number;
  /** Shown in the app's "Neuigkeiten" dialog (header icon). */
  showInApp: boolean;
  /** First time "In der App anzeigen" was switched on — drives the unread dot. */
  inAppPublishedAt: string | null;
  deliveries: { sent: number; failed: number; sending: number };
  /** Id of the combined e-mail this release was rolled into (then never sent on its own), else null. */
  combinedInto: string | null;
  /** For a combined e-mail: the ids of the releases it contains, oldest first; else []. */
  combinedFrom: string[];
}

export interface EmailDeliveryMeta {
  available: boolean;
  transport: string;
}

export interface ReleaseNoteList {
  notes: ReleaseNote[];
  email: EmailDeliveryMeta;
  subscriberCount: number;
}

export interface ReleaseNoteDetail {
  note: ReleaseNote;
  pendingRecipients: number;
  email: EmailDeliveryMeta;
}

export interface ReleaseNoteText {
  titleDe: string;
  bodyDe: string;
  titleEn: string;
  bodyEn: string;
}

const BASE = '/api/v1/admin/release-notes';

/** Fills the combination fields when a response has none (an API from before the feature). */
function normalize(note: ReleaseNote): ReleaseNote {
  return { ...note, combinedInto: note.combinedInto ?? null, combinedFrom: note.combinedFrom ?? [] };
}

export async function fetchReleaseNotes(): Promise<ReleaseNoteList> {
  const res = await apiClient.get<{ data: ReleaseNote[]; meta: { email: EmailDeliveryMeta; subscriberCount: number } }>(BASE);
  return { notes: res.data.map(normalize), email: res.meta.email, subscriberCount: res.meta.subscriberCount };
}

export async function fetchReleaseNote(id: string): Promise<ReleaseNoteDetail> {
  const res = await apiClient.get<{ data: ReleaseNote; meta: { pendingRecipients: number; email: EmailDeliveryMeta } }>(
    `${BASE}/${encodeURIComponent(id)}`,
  );
  return { note: normalize(res.data), pendingRecipients: res.meta.pendingRecipients, email: res.meta.email };
}

export async function createReleaseNote(input: ReleaseNoteText & { version?: string }): Promise<ReleaseNote> {
  const res = await apiClient.post<{ data: ReleaseNote }>(BASE, input);
  return normalize(res.data);
}

export async function updateReleaseNote(id: string, input: Partial<ReleaseNoteText>): Promise<ReleaseNote> {
  const res = await apiClient.patch<{ data: ReleaseNote }>(`${BASE}/${encodeURIComponent(id)}`, input);
  return normalize(res.data);
}

export async function regenerateReleaseNote(id: string): Promise<ReleaseNote> {
  const res = await apiClient.post<{ data: ReleaseNote }>(`${BASE}/${encodeURIComponent(id)}/regenerate`);
  return normalize(res.data);
}

export async function sendTestReleaseNote(id: string, language: 'de' | 'en'): Promise<void> {
  await apiClient.post(`${BASE}/${encodeURIComponent(id)}/test`, { language });
}

/** "In der App anzeigen" (409 INCOMPLETE without the DE and EN text). */
export async function setReleaseNoteInApp(id: string, show: boolean): Promise<ReleaseNote> {
  const res = await apiClient.put<{ data: ReleaseNote }>(`${BASE}/${encodeURIComponent(id)}/in-app`, { show });
  return normalize(res.data);
}

export async function sendReleaseNote(id: string): Promise<ReleaseNote> {
  const res = await apiClient.post<{ data: ReleaseNote }>(`${BASE}/${encodeURIComponent(id)}/send`);
  return normalize(res.data);
}

/** Rolls 2–20 unsent releases into one new draft e-mail (one section per release). */
export async function combineReleaseNotes(ids: readonly string[]): Promise<ReleaseNote> {
  const res = await apiClient.post<{ data: ReleaseNote }>(`${BASE}/combine`, { ids });
  return normalize(res.data);
}

/** Undoes an unsent combination: deletes the combined draft, the releases are separate again. */
export async function dissolveCombinedReleaseNote(id: string): Promise<void> {
  await apiClient.post(`${BASE}/${encodeURIComponent(id)}/dissolve`);
}

// ─── Markdown subset (same as the e-mail renderer, backend emailTemplates.ts) ──

export type MarkdownBlock =
  | { type: 'heading'; text: string }
  | { type: 'list'; items: string[] }
  | { type: 'paragraph'; text: string };

const BULLET = /^\s*[-*•]\s+(.*)$/;
const HEADING = /^\s*#{1,6}\s+(.*)$/;

/** Headings, bullet lists and paragraphs — the subset release notes use. */
export function parseReleaseMarkdown(markdown: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] | null = null;
  const flushParagraph = (): void => {
    if (paragraph.length > 0) blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
    paragraph = [];
  };
  const flushList = (): void => {
    if (list && list.length > 0) blocks.push({ type: 'list', items: list });
    list = null;
  };
  for (const raw of markdown.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    const heading = HEADING.exec(line);
    const bullet = BULLET.exec(line);
    if (line.trim() === '') {
      flushParagraph();
      flushList();
    } else if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'heading', text: (heading[1] ?? '').trim() });
    } else if (bullet) {
      flushParagraph();
      if (!list) list = [];
      list.push((bullet[1] ?? '').trim());
    } else {
      flushList();
      paragraph.push(line.trim());
    }
  }
  flushParagraph();
  flushList();
  return blocks;
}
